// src/components/reports/BudgetReport.jsx
import React from 'react';
import { formatCurrency } from '../../utils/formatters';

const BudgetReport = ({ data }) => {
    if (!data) {
        return (
            <div className="bg-white rounded-2xl border p-8 text-center text-gray-400">
                Nessun dato disponibile
            </div>
        );
    }

    const utilization = parseFloat(data.budgetUtilization || 0);
    const isOverBudget = data.status === 'OVER_BUDGET';

    const barColor = utilization >= 100
        ? 'bg-red-500'
        : utilization >= 80
            ? 'bg-orange-400'
            : utilization >= 60
                ? 'bg-yellow-400'
                : 'bg-emerald-500';

    return (
        <div className="space-y-6 animate-fade-in">

            {/* Header */}
            <div className={`rounded-2xl p-6 text-white ${isOverBudget ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`}>
                <p className="text-sm font-medium uppercase tracking-wider opacity-80">Report Budget</p>
                <h2 className="text-2xl font-bold mt-1">{data.projectName}</h2>
                <div className="flex items-center gap-2 mt-2">
                    <span className="text-2xl">{isOverBudget ? '⚠️' : '✅'}</span>
                    <span className="font-semibold">{isOverBudget ? 'Sopra budget' : 'Sotto controllo'}</span>
                </div>

                {/* Barra utilizzo */}
                <div className="mt-5">
                    <div className="flex justify-between text-sm opacity-80 mb-1.5">
                        <span>Utilizzo Budget</span>
                        <span className="font-bold text-white">{utilization}%</span>
                    </div>
                    <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                        <div
                            className={`h-full ${barColor} transition-all duration-700`}
                            style={{ width: `${Math.min(utilization, 100)}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Metriche principali */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                    <p className="text-xs font-semibold text-blue-500 uppercase tracking-wider">Budget Stimato</p>
                    <p className="text-2xl font-bold text-blue-700 mt-1">{formatCurrency(data.estimatedBudget)}</p>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5">
                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wider">Importo Contratto</p>
                    <p className="text-2xl font-bold text-purple-700 mt-1">{formatCurrency(data.contractAmount)}</p>
                </div>
                <div className={`${isOverBudget ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'} border rounded-2xl p-5`}>
                    <p className={`text-xs font-semibold ${isOverBudget ? 'text-red-500' : 'text-emerald-500'} uppercase tracking-wider`}>Costo Attuale</p>
                    <p className={`text-2xl font-bold ${isOverBudget ? 'text-red-700' : 'text-emerald-700'} mt-1`}>{formatCurrency(data.actualCost)}</p>
                </div>
            </div>

            {/* Varianza e rimanente */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border rounded-2xl p-5">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Budget Rimanente</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(data.remainingBudget)}</p>
                    <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-emerald-400 rounded-full transition-all"
                            style={{ width: `${Math.max(0, 100 - utilization)}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{(100 - utilization).toFixed(1)}% del budget disponibile</p>
                </div>

                <div className={`border rounded-2xl p-5 ${isOverBudget ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}>
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Varianza Budget</p>
                    <p className={`text-3xl font-bold mt-2 ${isOverBudget ? 'text-red-700' : 'text-emerald-700'}`}>
                        {isOverBudget ? '-' : '+'}{formatCurrency(Math.abs(data.budgetVariance))}
                    </p>
                    <p className={`text-sm mt-1 ${isOverBudget ? 'text-red-500' : 'text-emerald-500'}`}>
                        {isOverBudget ? '⚠️ Budget superato' : '✅ Risparmio realizzato'}
                    </p>
                </div>
            </div>

            {/* Fondo contingenza */}
            {data.contingencyFund > 0 && (
                <div className="bg-white border rounded-2xl p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Fondo Contingenza</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(data.contingencyFund)}</p>
                        <p className="text-xs text-gray-400 mt-1">Riserva disponibile per imprevisti</p>
                    </div>
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-2xl">
                        🛡️
                    </div>
                </div>
            )}

            {/* Alert over budget */}
            {isOverBudget && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                        <span className="text-2xl flex-shrink-0">🚨</span>
                        <div>
                            <h4 className="font-semibold text-red-800">Budget Superato</h4>
                            <p className="text-sm text-red-600 mt-1">
                                Il progetto ha superato il budget di <strong>{formatCurrency(Math.abs(data.budgetVariance))}</strong>.
                            </p>
                            <ul className="mt-2 space-y-1 text-sm text-red-500 list-disc list-inside">
                                <li>Rivedi le spese non essenziali</li>
                                <li>Considera l'attivazione del fondo contingenza</li>
                                <li>Notifica il responsabile di progetto</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Previsioni */}
            {data.forecasts?.estimatedCompletion && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">📈</span>
                        <div>
                            <p className="font-semibold text-indigo-900">Previsione al Completamento</p>
                            <p className="text-indigo-700 font-bold text-xl mt-1">
                                {formatCurrency(data.forecasts.estimatedCompletion)}
                            </p>
                            <p className="text-xs text-indigo-400 mt-1">Basato sul ritmo attuale di spesa</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BudgetReport;